/**
 * <s-stock-split> — quantity-aware stock panel on product pages.
 *
 * Reads `data-stock` (units in stock) and `data-in-cart` (units of this variant
 * already in the cart) and watches the surrounding product form's quantity.
 * - quantity + in cart <= stock: "In stock" panel.
 * - some stock left but not enough: "Only X in stock, the other Y made to order".
 * - no stock left after the cart: "All X in stock are already in your cart".
 * Ordering is never blocked; the panel only tells the buyer what ships when.
 */
class SStockSplit extends HTMLElement {
  #ac;
  #form;

  connectedCallback() {
    this.#form = this.closest('form');
    if (!this.#form) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const update = () => this.#update();
    this.#form.addEventListener('input', update, opts);
    this.#form.addEventListener('change', update, opts);
    this.#form.addEventListener('s-quantity:change', update, opts);
    // After an add to cart, count those units as taken so the next add reflects them.
    this.#form.addEventListener('submit', () => {
      this.dataset.inCart = String(this.#inCart() + this.#quantity());
      requestAnimationFrame(update);
    }, opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #inCart() {
    return Math.max(0, parseInt(this.dataset.inCart, 10) || 0);
  }

  #quantity() {
    const input = this.#form.querySelector('input[name="quantity"]');
    return Math.max(1, parseInt(input?.value, 10) || 1);
  }

  #update() {
    const stock = parseInt(this.dataset.stock, 10) || 0;
    const left = stock - this.#inCart();
    const qty = this.#quantity();

    let state = 'in_stock';
    if (left <= 0) state = 'full';
    else if (qty > left) state = 'split';

    for (const panel of this.querySelectorAll('[data-split-panel]')) {
      panel.hidden = panel.dataset.splitPanel !== state;
    }

    if (state === 'split') {
      const title = this.querySelector('[data-split-title]');
      const body = this.querySelector('[data-split-body]');
      if (title) title.textContent = title.dataset.template.replace('[count]', left);
      if (body) {
        body.textContent = body.dataset.template
          .replace('[in_stock]', left)
          .replace('[made]', qty - left);
      }
    }
  }
}

if (!customElements.get('s-stock-split')) customElements.define('s-stock-split', SStockSplit);
